import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { UserPlus, Trash2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

// Card no Admin/Sistema: cria/apaga a conta DEMO usada pelo revisor do Google Play
const DemoUserPanel = () => {
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [info, setInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/demo-user/status`, { headers: authHeaders() });
      setInfo(data);
    } catch (e) {
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createDemo = async () => {
    setWorking(true);
    try {
      const { data } = await axios.post(`${API}/admin/demo-user/create`, {}, { headers: authHeaders() });
      toast.success(data.already_existed ? 'Conta DEMO já existia' : '✅ Conta DEMO criada com bill e agenda de exemplo!');
      await load();
    } catch (e) {
      toast.error('Falha ao criar conta DEMO');
    } finally {
      setWorking(false);
    }
  };

  const deleteDemo = async () => {
    if (!window.confirm('Apagar a conta DEMO e TODOS os dados criados por ela (bills, tickets, notificações...)? Essa ação não pode ser desfeita.')) return;
    setWorking(true);
    try {
      const { data } = await axios.delete(`${API}/admin/demo-user`, { headers: authHeaders() });
      const total = Object.values(data.deleted_data || {}).reduce((a, b) => a + b, 0);
      toast.success(`🗑️ Conta DEMO apagada${total ? ` + ${total} registro(s) de dados` : ''}`);
      await load();
    } catch (e) {
      toast.error('Falha ao apagar conta DEMO');
    } finally {
      setWorking(false);
    }
  };

  const dataCount = info?.data_counts ? Object.values(info.data_counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4" data-testid="demo-user-panel">
      <h3 className="font-bold text-amber-900 flex items-center gap-2 mb-1">
        🤖 Conta Demo (Revisão Google Play)
      </h3>
      <p className="text-sm text-amber-800 mb-3">
        Conta de teste para o revisor do Google entrar no app (já vem com 1 bill e 1 agenda de exemplo). Sem acesso a dados de outros usuários.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-amber-700"><Loader2 className="w-4 h-4 animate-spin" /> Carregando...</div>
      ) : info?.exists ? (
        <div className="space-y-3">
          <div className="bg-white border border-amber-200 rounded-md p-3 text-sm font-mono" data-testid="demo-user-credentials">
            <div>👤 Usuário: <strong>{info.username}</strong></div>
            <div>🔑 Senha: <strong>{info.password}</strong></div>
            {dataCount > 0 && (
              <div className="mt-1 text-xs text-amber-700 font-sans">📦 {dataCount} registro(s) de dados criados pela conta</div>
            )}
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={deleteDemo}
            disabled={working}
            data-testid="demo-user-delete-btn"
          >
            {working ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Apagar conta teste (e todos os dados dela)
          </Button>
        </div>
      ) : (
        <Button size="sm" onClick={createDemo} disabled={working} data-testid="demo-user-create-btn">
          {working ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
          Criar usuário demo
        </Button>
      )}
    </div>
  );
};

export default DemoUserPanel;
